import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Control, Controller, useController } from "react-hook-form";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  FormHelperText,
} from "@mui/material";

import { CreatePostFormData } from "../../../api/types/feed.types";
import { commonStyles } from "../../../pages/CreatePostPage/components/CreatePostForm/index";

import { InviteModal } from "./InviteModal";
interface PrivacyFieldProps {
  control: Control<CreatePostFormData>;
  name: "privacy";
  error?: string;
}

export const PrivacyField = ({ control, name, error }: PrivacyFieldProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const { field: selectedUsersField } = useController({
    name: "selectedUsers",
    control,
    defaultValue: [],
  });

  const { field: selectedGroupsField } = useController({
    name: "selectedGroups",
    control,
    defaultValue: [],
  });

  const handlePrivacyChange = (value: string, onChange: (value: string) => void) => {
    onChange(value);
    if (value === "PRIVATE") {
      setIsInviteModalOpen(true);
    }
  };

  const privacyOptions = [
    {
      value: "PUBLIC",
      label: t("public"),
      description: t("publicDescription"),
    },
    {
      value: "PRIVATE",
      label: t("private"),
      description: t("privateDescription"),
    },
    {
      value: "CLOSED",
      label: t("closed"),
      description: t("closedDescription"),
    },
  ];

  return (
    <FormControl error={!!error}>
      <FormLabel sx={{ ...commonStyles.formLabel, mb: "8px" }}>{t("postPrivacy")}</FormLabel>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            value={value}
            onChange={(e) => handlePrivacyChange(e.target.value, onChange)}
            sx={commonStyles.radioGroup}
          >
            {privacyOptions.map(({ value, label, description }) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" sx={{ p: 0 }} />}
                sx={{
                  alignItems: "flex-start",
                  gap: "8px",
                  m: 0,
                }}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      gap: "2px",
                      alignItems: { xs: "flex-start", sm: "flex-end" },
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Typography sx={commonStyles.radioLabel}>{label}</Typography>
                    <Typography sx={commonStyles.radioDescription}>{description}</Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        )}
      />

      <InviteModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        selectedUserIds={selectedUsersField.value || []}
        onSelectedUserIdsChange={selectedUsersField.onChange}
        selectedGroupIds={selectedGroupsField.value || []}
        onSelectedGroupIdsChange={selectedGroupsField.onChange}
      />

      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
